/**
 * runForecastCalc — extraído de ForecastPage.tsx (P7 split).
 *
 * Calcula la sugerencia de compra para los productos seleccionados que
 * tengan demanda neta > 0. Llama 2 endpoints en paralelo (allSettled):
 *   - /api/forecasting/calc          → resultados por producto
 *   - /distribution/seeding/samples-by-products → stock pendiente/aprobado
 *
 * Recibe setters como callbacks para mantener la lógica pura. La página
 * envuelve esta función en un useCallback.
 */

import { ProductData } from "./types";
import { apiClient } from "../../services/apiClient";
import MarginsService from "../../services/margins.service";
import {
  type CalcResult,
  type SamplesByProduct,
} from "../../components/Forecast/SelectedProductsDialog";

export type { CalcResult, SamplesByProduct };

interface RunForecastCalcOptions {
  months: number;
  overrideIds?: Set<string> | undefined;
  selectedIds: Set<string>;
  products: ProductData[];
  parentGlobalMarginRatio: number | null;
  setParentCalcResults: (r: CalcResult[]) => void;
  setParentSamplesByProduct: (s: SamplesByProduct) => void;
  setParentCalcLoading: (b: boolean) => void;
  setSelectedIds: (
    updater: (prev: Set<string>) => Set<string>,
  ) => void;
  setParentGlobalMarginRatio: (n: number | null) => void;
}

export async function runForecastCalc(
  opts: RunForecastCalcOptions,
): Promise<void> {
  const {
    months,
    overrideIds,
    selectedIds,
    products,
    parentGlobalMarginRatio,
    setParentCalcResults,
    setParentSamplesByProduct,
    setParentCalcLoading,
    setSelectedIds,
    setParentGlobalMarginRatio,
  } = opts;

  // Use override IDs when provided (avoids stale state from async setSelectedIds)
  const activeIds = overrideIds ?? selectedIds;
  // Solo descartamos productos no seleccionados o con compras desactivadas.
  // El backend decide si hay demanda + muestras a comprar; un producto con
  // inventario suficiente para la demanda regular puede aún tener muestras
  // de distribución pendientes que justifican incluirlo en el draft.
  const productIds = products
    .filter((p) => {
      if (!activeIds.has(p.unifiedProductId)) return false;
      if (p.activeForPurchase === false) return false;
      return true;
    })
    .map((p) => p.unifiedProductId);

  if (productIds.length === 0) {
    setParentCalcResults([]);
    setParentSamplesByProduct({});
    return;
  }

  try {
    setParentCalcLoading(true);
    // allSettled: una falla en samples NO debe descartar el response del forecast.
    const [forecastSettled, samplesSettled] = await Promise.allSettled([
      apiClient.post<{ status: string; data: any[] }>("/forecasting/calc", {
        productIds,
        months,
        packOverrides: [],
      }),
      apiClient.post<{
        data: Record<
          string,
          { pending: number; approved: number; total: number }
        >;
      }>("/distribution/seeding/samples-by-products", { productIds }),
    ]);

    let samplesData: SamplesByProduct = {};
    if (
      samplesSettled.status === "fulfilled" &&
      samplesSettled.value.data?.data
    ) {
      samplesData = samplesSettled.value.data.data;
    }
    setParentSamplesByProduct(samplesData);

    if (forecastSettled.status === "fulfilled") {
      const forecastJson = forecastSettled.value.data;
      if (
        forecastJson?.status === "success" &&
        Array.isArray(forecastJson.data)
      ) {
        const results: CalcResult[] = forecastJson.data.map((x: any) => ({
          id: String(x.id),
          suggestedPurchaseQty: Number(x.suggestedPurchaseQty || 0),
          forecastMethod: x.forecastMethod,
          details: x.details,
          warnings: x.warnings,
          forecast_calculated_at: x.forecast_calculated_at,
          economics: x.economics,
        }));

        const stalenessWarnings = results.flatMap(
          (r) =>
            r.warnings?.filter((w) => w.includes("desactualizado")) ?? [],
        );
        if (stalenessWarnings.length > 0) {
          console.warn(
            "[forecast] staleness detected:",
            stalenessWarnings[0],
          );
        }

        setParentCalcResults(results);

        // Mantener seleccionados los que tengan algo a comprar:
        // demanda calculada (baseSuggested > 0) O muestras pendientes (>0).
        // Productos sin ninguno de los dos no aportan línea al draft.
        const validIds = new Set(
          results
            .filter((r: any) => {
              const base = r.details?.baseSuggested ?? 0;
              const samples = samplesData[String(r.id)]?.total ?? 0;
              return base > 0 || samples > 0;
            })
            .map((r: any) => String(r.id)),
        );
        setSelectedIds((prev) => {
          const next = new Set<string>();
          for (const id of prev) {
            if (validIds.has(id)) next.add(id);
          }
          return next;
        });
      } else {
        setParentCalcResults([]);
      }
    } else {
      setParentCalcResults([]);
    }

    // Lazy-load global margin ratio once
    if (parentGlobalMarginRatio === null) {
      try {
        const marginRes = await MarginsService.getGlobalMarginPerCostRatio(
          3,
          true,
        );
        if (marginRes && typeof marginRes.ratio === "number")
          setParentGlobalMarginRatio(marginRes.ratio);
      } catch {
        setParentGlobalMarginRatio(0);
      }
    }
  } catch {
    setParentCalcResults([]);
    setParentSamplesByProduct({});
  } finally {
    setParentCalcLoading(false);
  }
}
